import React, { useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { forgotPassword, resetPassword, isAuthenticated, errorCode } from 'services/auth.service'
import AuthLayout, { authInputClass, authLabelClass, authButtonClass } from 'components/AuthLayout'
import CodeInput, { CODE_LENGTH, emptyCode } from 'components/CodeInput'
import ResendCode from 'components/ResendCode'
import PasswordStrength, { MIN_PASSWORD } from 'components/PasswordStrength'
import { useI18n, apiError } from 'i18n'

const CODE_ERRORS = ['CODE_INVALID', 'CODE_EXPIRED', 'CODE_LOCKED']

/**
 * Two steps on one page: ask for the email → enter the emailed code and a new
 * password. Success signs the user in.
 */
const ForgotPassword = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { t } = useI18n()
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState(params.get('email') || '')
  const [digits, setDigits] = useState(emptyCode)
  const [password, setPassword] = useState('')
  const [codeError, setCodeError] = useState('')
  const [busy, setBusy] = useState(false)
  const passwordRef = useRef(null)

  if (isAuthenticated()) return <Navigate to='/' replace />

  const sendCode = async () => {
    await forgotPassword(email.trim())
  }

  const onRequest = async (event) => {
    event.preventDefault()
    setBusy(true)
    try {
      await sendCode()
      setStep('reset')
    } catch (error) {
      toast.error(apiError(error, t('forgot.sendFailed')))
    } finally {
      setBusy(false)
    }
  }

  const code = digits.join('')
  const canReset = code.length === CODE_LENGTH && password.length >= MIN_PASSWORD && !busy

  const onReset = async (event) => {
    event.preventDefault()
    if (!canReset) return
    setBusy(true)
    setCodeError('')
    try {
      await resetPassword(email.trim(), code, password)
      toast.success(t('forgot.done'))
      navigate('/', { replace: true })
    } catch (error) {
      if (CODE_ERRORS.includes(errorCode(error))) {
        setCodeError(apiError(error, t('verify.failed')))
        setDigits(emptyCode())
        return
      }
      toast.error(apiError(error, t('forgot.resetFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onResend = async () => {
    try {
      await sendCode()
      toast.success(t('verify.resent'))
      setCodeError('')
      setDigits(emptyCode())
      return true
    } catch (error) {
      toast.error(apiError(error, t('verify.resendFailed')))
      return false
    }
  }

  const footer = <Link to='/login' className='font-semibold text-accent hover:underline'>{t('forgot.backToLogin')}</Link>

  if (step === 'email') {
    return (
      <AuthLayout subtitle={t('forgot.subtitle')} onSubmit={onRequest} footer={footer}>
        <label htmlFor='fp-email' className={authLabelClass}>{t('login.email')}</label>
        <input id='fp-email' type='email' autoComplete='email' required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
          className={`mb-6 ${authInputClass}`} />
        <button type='submit' disabled={busy || !email.trim()} className={authButtonClass}>
          {busy ? t('forgot.sending') : t('forgot.send')}
        </button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      subtitle={<>{t('forgot.sentTo')} <strong className='text-slate-800'>{email.trim()}</strong></>}
      onSubmit={onReset}
      footer={footer}
    >
      <CodeInput digits={digits} onChange={(next) => { setDigits(next); setCodeError('') }}
        onComplete={() => passwordRef.current?.focus()} error={codeError} disabled={busy} />
      <p role='alert' className='mt-3 min-h-[1.25rem] text-center text-sm text-status-failed'>{codeError}</p>
      <p className='mb-5 text-center text-xs text-slate-500'>
        {t('forgot.noAccountHint')}{' '}
        <button type='button' onClick={() => { setStep('email'); setDigits(emptyCode()); setCodeError('') }}
          className='font-semibold text-brand hover:underline'>{t('forgot.changeEmail')}</button>
      </p>

      <label htmlFor='fp-password' className={authLabelClass}>{t('forgot.newPassword')}</label>
      <input id='fp-password' ref={passwordRef} type='password' required minLength={MIN_PASSWORD} autoComplete='new-password'
        value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby='fp-password-hint' className={authInputClass} />
      <PasswordStrength id='fp-password-hint' value={password} />

      <button type='submit' disabled={!canReset} className={authButtonClass}>
        {busy ? t('common.saving') : t('forgot.submit')}
      </button>
      <ResendCode onResend={onResend} />
    </AuthLayout>
  )
}

export default ForgotPassword
